import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import withAuth from "../../utils/withAuth";
import { fetchCallAllDataByIDAPI } from "../../utils/api";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";

const SKELETON_COUNT = 10;

const SkeletonTableCard = () => (
    <div className="col-md-4 col-lg-3 col-sm-6 mb-4">
        <div className="card h-100 shadow-sm">
            <div className="card-body p-2">
                <div className="skeleton" style={{ height: 18, width: "70%", marginBottom: 10 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "100%", marginBottom: 6 }} />
                <div className="skeleton" style={{ height: 14, width: "80%", marginBottom: 6 }} />
            </div>
        </div>
    </div>
);

const YesterdayTeamCall = () => {
    const [process, setProcess] = useState([]);   // rows
    const [summary, setSummary] = useState(null); // summary
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");

    useEffect(() => {
        if (!ppcid) return navigate("/");
        navigate("/yesterday-team-call-report");
        getLiveCallWithProcessName(ppcid);
    }, [ppcid]);

    const getLiveCallWithProcessName = async (id) => {
        setIsLoading(true);
        try {
            const res = await fetchCallAllDataByIDAPI(
                `ppc-team-get-yesterday-call-with-proccess-name-by-id/${id}`
            );

            if (res?.data?.status === 1) {
                // ✅ backend now returns: process: { rows:[], summary:{} }
                const p = res?.data?.process || {};
                setProcess(Array.isArray(p?.rows) ? p.rows : []);
                setSummary(p?.summary || null);
            } else {
                toast.error(res?.data?.message || "Failed to load process");
                setProcess([]);
                setSummary(null);
            }
        } catch (e) {
            console.error(e);
            toast.error("Network/CORS error while loading process");
            setProcess([]);
            setSummary(null);
        } finally {
            setIsLoading(false);
        }
    };

    // Suffix → Person/Group name map (extend freely)
    const PERSON_BY_CODE = {
        K: "Vivek Kumar",
        R: "Pragya Singh",
        J: "Akash Deep",
        V: "Pradeep Verma",
        T: "Yanshi",
        N: "Deepika Sisodia",
        D: "Deepak Kumar Giri",
        S: "Sumit Saini",
        I: "Ranjan Kumar",
        B: "Navin Chaurasia",
        E: "Bhumika Gola",
        G: "Vimal Jadon",
        P: "Anurodh Baral",
        M: "Simrati Kaur",
        Z: "Manprit Sir",
        A: "Ankita",
        W: "Ishika Pokhriyal",
        U: "Om Kumar",
    };

    const PREFER_PREFIX = false;

    const deriveGroupName = (skillRaw) => {
        const skill = String(skillRaw || "").trim();
        if (!skill) return "Other";

        const parts = skill.split("_").filter(Boolean);
        const n = parts.length;

        const first = (parts[0] || "").toUpperCase();
        const last = (parts[n - 1] || "").toUpperCase();
        const last2 = n >= 2 ? `${(parts[n - 2] || "").toUpperCase()}_${last}` : null;

        if (PREFER_PREFIX) {
            if (PERSON_BY_CODE[first]) return PERSON_BY_CODE[first];
            if (first) return first;
        }

        if (last2 && PERSON_BY_CODE[last2]) return PERSON_BY_CODE[last2];
        if (PERSON_BY_CODE[last]) return PERSON_BY_CODE[last];

        if (PERSON_BY_CODE[first]) return PERSON_BY_CODE[first];
        if (first) return first;

        return "Other";
    };

    const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
    const fmtMoney = (n) =>
        Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // ✅ Grouped with Calls + Orders + Spend
    const groupedData = useMemo(() => {
        const groups = {};
        (process || []).forEach((row) => {
            const code = String(row?.skillname || "");
            const calls = Number(row?.unique_calls || 0);
            const orders = Number(row?.order_count || 0);
            const spend = Number(row?.spend_yesterday || 0);
            const groupName = deriveGroupName(code);

            if (!groups[groupName]) {
                groups[groupName] = { name: groupName, totalCalls: 0, totalOrders: 0, totalSpend: 0, items: [] };
            }
            groups[groupName].totalCalls += calls;
            groups[groupName].totalOrders += orders;
            groups[groupName].totalSpend += spend;
            groups[groupName].items.push({ code, calls, orders, spend });
        });

        Object.values(groups).forEach((g) => {
            g.perf = g.totalCalls ? (g.totalOrders / g.totalCalls) * 100 : 0;
            g.items.sort((a, b) => (b.calls !== a.calls ? b.calls - a.calls : a.code.localeCompare(b.code)));
        });

        return Object.values(groups).sort((a, b) =>
            b.totalCalls !== a.totalCalls ? b.totalCalls - a.totalCalls : a.name.localeCompare(b.name)
        );
    }, [process]);

    // ✅ Prefer backend summary if present
    const totalCalls = useMemo(() => {
        if (summary && typeof summary.total_unique_calls !== "undefined") return Number(summary.total_unique_calls) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.unique_calls) || 0), 0);
    }, [process, summary]);

    const totalOrders = useMemo(() => {
        if (summary && typeof summary.total_orders_created !== "undefined") return Number(summary.total_orders_created) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.order_count) || 0), 0);
    }, [process, summary]);

    const totalSpend = useMemo(() => {
        if (summary && typeof summary.total_spend_yesterday !== "undefined") return Number(summary.total_spend_yesterday) || 0;
        return (process || []).reduce((s, r) => s + (Number(r?.spend_yesterday) || 0), 0);
    }, [process, summary]);

    const perf = useMemo(() => (totalCalls ? (totalOrders / totalCalls) * 100 : 0), [totalCalls, totalOrders]);

    return (
        <div className="misDashboard">
            <Helmet>
                <title>SATKARTAR::DASHBOARD</title>
            </Helmet>

            <style>{`
        .skeleton{position:relative;overflow:hidden;background:#eee;border-radius:6px}
        .skeleton:after{content:"";position:absolute;inset:0;transform:translateX(-100%);
          background:linear-gradient(90deg,transparent,rgba(255,255,255,.6),transparent);
          animation:shimmer 1.2s infinite}
        @keyframes shimmer{100%{transform:translateX(100%)}}
        .mini-table th,.mini-table td{font-size:12px; padding:6px 8px;}
      `}</style>
            <style>{`
        .badge.text-bg-light { font-weight: 600; border: 1px solid #e6e9f2; }
      `}</style>

            <ContentWrapper>
                <div className="row">
                    <div className="col-12">
                        <ReportNavBar />
                    </div>
                </div>

                <div className="">
                    <h5 className="livecallcount">
                        Yesterday:
                        {"  "}Calls: {isLoading ? "..." : fmt(totalCalls)}
                        {"  |  "}
                        Orders: {isLoading ? "..." : fmt(totalOrders)}
                        {"  |  "}
                        Spend: {isLoading ? "..." : `₹${fmtMoney(totalSpend)}`}
                        {"  |  "}
                        Perf: {isLoading ? "..." : `${perf.toFixed(1)}%`}
                    </h5>
                </div>

                <div className="row mt-3 livecallbody">
                    {isLoading
                        ? Array.from({ length: SKELETON_COUNT }).map((_, i) => <SkeletonTableCard key={`sk-${i}`} />)
                        : groupedData.map((group, idx) => (
                            <div className="col-md-6 col-lg-4 mb-3" key={idx}>
                                <div className="card shadow-sm">
                                    <div className="card-body p-2">
                                        {/* ✅ make card body a scroll container */}
                                        <div className="p-0" style={{ maxHeight: 260, overflowY: "auto" }}>

                                            <table className="table table-sm table-bordered mb-0 mini-table">
                                                <thead className="stickyHead">
                                                    {/* ✅ Sticky Header like your screenshot */}
                                                    <tr>
                                                        <th colSpan={5} className="skillStickyHeader">
                                                            <div className="d-flex justify-content-between">
                                                                <div className="sh-left">{group.name}</div>
                                                                <div className="sh-right">
                                                                    <span>Calls: <b>{fmt(group.totalCalls)}</b></span>
                                                                    <span>| Orders: <b>{fmt(group.totalOrders)}</b></span>
                                                                    <span>| ₹{fmtMoney(group.totalSpend)}</span>
                                                                </div>
                                                            </div>
                                                        </th>
                                                    </tr>

                                                    {/* ✅ Column titles */}
                                                    <tr className="colHead">
                                                        <th>Skill</th>
                                                        <th style={{ width: 30, textAlign: "right" }}>Calls</th>
                                                        <th style={{ width: 30, textAlign: "right" }}>Orders</th>
                                                        <th style={{ width: 60, textAlign: "right" }}>Spend</th>
                                                        <th style={{ width: 45, textAlign: "right" }}>Perf</th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {group.items.map((item, ix) => (
                                                        <tr key={ix}>
                                                            <td>{item.code}</td>
                                                            <td style={{ textAlign: "right" }}>{fmt(item.calls)}</td>
                                                            <td style={{ textAlign: "right" }}>{fmt(item.orders)}</td>
                                                            <td style={{ textAlign: "right" }}>₹{fmtMoney(item.spend)}</td>
                                                            <td
                                                                style={{
                                                                    textAlign: "right",
                                                                    fontWeight: 800,
                                                                    display: "flex",
                                                                    justifyContent: "flex-end",
                                                                    alignItems: "center",
                                                                    gap: 4,
                                                                    color:
                                                                        item.calls === 0
                                                                            ? "#999"
                                                                            : (item.orders / item.calls) >= 0.15
                                                                                ? "#2e7d32"
                                                                                : "#d32f2f",
                                                                }}
                                                            >
                                                                {item.calls === 0 ? (
                                                                    <>
                                                                        <span style={{ fontSize: 12 }}>—</span>
                                                                        <span>0.0%</span>
                                                                    </>
                                                                ) : (item.orders / item.calls) >= 0.15 ? (
                                                                    <>
                                                                        <span style={{ fontSize: 12 }}>↑</span>
                                                                        <span>{((item.orders / item.calls) * 100).toFixed(1)}%</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <span style={{ fontSize: 12 }}>↓</span>
                                                                        <span>{((item.orders / item.calls) * 100).toFixed(1)}%</span>
                                                                    </>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                    {!isLoading && groupedData.length === 0 && (
                        <div className="col-12">
                            <div className="alert alert-info mb-0">No data for yesterday.</div>
                        </div>
                    )}
                </div>
            </ContentWrapper>
        </div>
    );
};

export default withAuth(YesterdayTeamCall);
